/**
 * App.tsx - the composition root.
 *
 * This file is the seam where the three modules (foundation, twin,
 * framework) come together into one route table. It lives at the
 * frontend root (peer of main.tsx) - NOT under shared/ - for the
 * same reason backend/main.py lives at the backend root: the
 * composition root is the only place allowed to know about every
 * module by name. shared/ holds cross-cutting primitives; App.tsx
 * is the wiring, not a primitive.
 *
 * If you find yourself wanting to import a feature module from
 * shared/, the right answer is almost always 'lift it up here
 * (or to main.tsx) instead'. The ESLint module-boundary rule
 * (P0.8 + docs/module-boundaries.md) enforces that distinction.
 *
 * Phase 1 will add the Preparer / Reviewer / Approver position-queue
 * affordances around these routes; for now each module just shows
 * its hello-world.
 */
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppShell } from "shared/layout/AppShell";
import { RegimeTerms } from "shared/regime/RegimeTerms";
import { ToastHost, ToastProvider } from "shared/toast/ToastProvider";
import { UsersView } from "shared/admin/UsersView";
import { OpsResHome } from "twin/home/OpsResHome";
import { ServiceOwnerHome } from "twin/home/ServiceOwnerHome";
import { AccountableExecutiveNote } from "twin/home/AccountableExecutiveNote";
import { canRunOperations, ownsSomething } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { AuditEventsView } from "shared/admin/AuditEventsView";
import { ConnectorsView } from "shared/admin/ConnectorsView";
import { SearchView } from "shared/search/SearchView";
import { AdminHome } from "shared/admin/AdminHome";
import { SettingsAdminView } from "shared/admin/SettingsAdminView";
import { ConnectionSetupView } from "shared/admin/ConnectionSetupView";
import { BankSetupView } from "shared/admin/BankSetupView";
import { ObligationEngineView } from "shared/admin/ObligationEngineView";
import { TwinDeriveView } from "shared/admin/TwinDeriveView";
import { EngineRunsConsole } from "shared/admin/EngineRunsConsole";
import { EngineActivityLedgerView } from "shared/admin/EngineActivityLedgerView";
import { TwinCalibrationView } from "shared/admin/TwinCalibrationView";
import { DataDictionaryView } from "shared/admin/DataDictionaryView";
import { DataBaselineView } from "shared/admin/DataBaselineView";
import { EdgeDelaysView } from "shared/admin/EdgeDelaysView";
import { TriggersView } from "shared/admin/TriggersView";
import { DataImportView } from "shared/admin/DataImportView";
import { DocumentsView } from "shared/documents/DocumentsView";
import { FrameworkImportView } from "shared/admin/FrameworkImportView";
import { ServiceAccountsView } from "shared/admin/ServiceAccountsView";
import { ExaminerGrantsView } from "shared/admin/ExaminerGrantsView";
import { MockExamView } from "shared/admin/MockExamView";
import { AssuranceCard } from "shared/assurance/AssuranceCard";
import { AssuranceView } from "shared/assurance/AssuranceView";
import { RecommendationsCard } from "shared/recommendations/RecommendationsCard";
import { RecommendationsView } from "shared/recommendations/RecommendationsView";
import { BaselCrosswalkView } from "shared/readiness/BaselCrosswalkView";
import { ReadinessCard } from "shared/readiness/ReadinessCard";
import { ReadinessView } from "shared/readiness/ReadinessView";
import { MyWorkCard } from "shared/work/MyWorkCard";
import { ExecutiveHome, canSeeExecutive } from "shared/executive/ExecutiveHome";
import { GlossaryView } from "shared/glossary/GlossaryView";
import { NotFoundView } from "shared/executive/NotFoundView";
import { MyWorkView } from "shared/work/MyWorkView";
import { LicensePanel } from "shared/licensing/LicensePanel";
import { BenchmarkCard } from "shared/benchmark/BenchmarkCard";
import { SectorBenchmarkView } from "shared/benchmark/SectorBenchmarkView";
import { BoardSnapshotCard } from "shared/board/BoardSnapshotCard";
import { BoardSnapshotView } from "shared/board/BoardSnapshotView";
import { DossierCard } from "shared/home/DossierCard";
import { JourneyRedirect, JourneyView } from "shared/journey/JourneyView";
import { FiveQuestions } from "shared/home/FiveQuestions";
import { ScoreMethodologyView } from "shared/readiness/ScoreMethodologyView";
import { Home } from "shared/layout/Home";
import { GettingStartedView } from "shared/onboarding/GettingStartedView";
import { FoundationHome } from "foundation/FoundationHome";
import { CapabilityAdminPage } from "foundation/admin/CapabilityAdminPage";
import { ChannelAdminPage } from "foundation/admin/ChannelAdminPage";
import { ConfigurationItemAdminPage } from "foundation/admin/ConfigurationItemAdminPage";
import { IBSAdminPage } from "foundation/admin/IBSAdminPage";
import { JourneyAdminPage } from "foundation/admin/JourneyAdminPage";
import { ProductAdminPage } from "foundation/admin/ProductAdminPage";
import { OutsourcingContractAdminPage } from "foundation/admin/OutsourcingContractAdminPage";
import { DataAssetAdminPage } from "foundation/admin/DataAssetAdminPage";
import { FundingResourceAdminPage } from "foundation/admin/FundingResourceAdminPage";
import { EaImportAdminPage } from "foundation/admin/EaImportAdminPage";
import { FloorListAdminPage } from "foundation/admin/FloorListAdminPage";
import { IBSGroupAdminPage } from "foundation/admin/IBSGroupAdminPage";
import { RegulatoryCommitmentAdminPage } from "foundation/admin/RegulatoryCommitmentAdminPage";
import { FacilityAdminPage } from "foundation/admin/FacilityAdminPage";
import { RecoveryTeamAdminPage } from "foundation/admin/RecoveryTeamAdminPage";
import { RiskAppetiteAdminPage } from "foundation/admin/RiskAppetiteAdminPage";
import { ServiceAdminPage } from "foundation/admin/ServiceAdminPage";
import { VendorAdminPage } from "foundation/admin/VendorAdminPage";
import { MvbDeclarationDetail } from "foundation/mvb/MvbDeclarationDetail";
import { MvbDeclarationsRegister } from "foundation/mvb/MvbDeclarationsRegister";
import { MvbScorecardCard } from "foundation/mvb/MvbScorecardCard";
import { AssessmentCyclesCard } from "framework/home/AssessmentCyclesCard";
import { CAPDetailView } from "framework/CAPDetailView";
import { FrameworkHome } from "framework/FrameworkHome";
import { LetterDetailView } from "framework/LetterDetailView";
import { LetterIntakeView } from "framework/LetterIntakeView";
import { LettersRegisterView } from "framework/LettersRegisterView";
import { PositionQueueWidget } from "framework/PositionQueueWidget";
import { QueueView } from "framework/QueueView";
import { CascadeView } from "twin/CascadeView";
import { CascadeHomeCard } from "twin/cascade/CascadeHomeCard";
import { SinceLastLooked } from "twin/whatsnew/SinceLastLooked";
import { HomeHero } from "twin/home/HomeHero";
import { ConstellationView } from "twin/constellation/ConstellationView";
import { ResilienceTrendCard } from "twin/home/ResilienceTrendCard";
import { CapabilityDetail } from "twin/entity/CapabilityDetail";
import { ConfigurationItemDetail } from "twin/entity/ConfigurationItemDetail";
import { IBSDetail } from "twin/entity/IBSDetail";
import { DrillsView } from "twin/drills/DrillsView";
import { IBSAdmissionView } from "twin/ibs/IBSAdmissionView";
import { CifRegisterView } from "twin/cif/CifRegisterView";
import { ChangeRegisterView } from "twin/changes/ChangeRegisterView";
import { FieldExamView } from "twin/fieldexam/FieldExamView";
import { IBSGroupsView } from "twin/groups/IBSGroupsView";
import { IncidentReportRegisterView } from "twin/incidents/IncidentReportRegisterView";
import { IBSPortfolioView } from "twin/portfolio/IBSPortfolioView";
import { RaiseFindingView } from "twin/finding/RaiseFindingView";
import { OpenFindingsView } from "twin/findings/OpenFindingsView";
import { PeopleView } from "shared/people/PeopleView";
import { MvbCandidatesView } from "twin/mvb/MvbCandidatesView";
import { MvbRecoveryView } from "twin/mvb/MvbRecoveryView";
import { TargetStateView } from "twin/mvb/TargetStateView";
import { ContinuityPostureView } from "twin/mvb/ContinuityPostureView";
import { TechnologyCurrencyView } from "twin/currency/TechnologyCurrencyView";
import { TechnologyLifecycleAdminPage } from "foundation/admin/TechnologyLifecycleAdminPage";
import { ServiceDispositionView } from "twin/portfolio/ServiceDispositionView";
import { ArchitectureStandardsView } from "twin/standards/ArchitectureStandardsView";
import { ContinuityPlansView } from "twin/continuity/ContinuityPlansView";
import { ToleranceMetricsView } from "twin/tolerance/ToleranceMetricsView";
import { MvbDrillView } from "twin/mvb/MvbDrillView";
import { ServiceDetail } from "twin/entity/ServiceDetail";
import { VendorDetail } from "twin/entity/VendorDetail";
import { EvidenceArtefactDetail } from "twin/evidence/EvidenceArtefactDetail";
import { FindingDetail } from "twin/finding/FindingDetail";
import { TwinHome } from "twin/TwinHome";
import { AnalyserPanel } from "framework/assessments/AnalyserPanel";
import { AttestationsView } from "framework/attestations/AttestationsView";
import { DocumentGapView } from "framework/gap/DocumentGapView";
import { OutboundFeedView } from "framework/OutboundFeedView";
import { RegulatoryChangeView } from "framework/regchange/RegulatoryChangeView";
import { CycleAssessmentGridView } from "framework/assessments/CycleAssessmentGridView";
import { FrameworkAssessmentsView } from "framework/assessments/FrameworkAssessmentsView";
import { BianLandscapeClassicView } from "twin/landscape/BianLandscapeClassicView";
import { BianLandscapeView } from "twin/landscape/BianLandscapeView";
import { KsaCapabilityMapView } from "twin/capmap/KsaCapabilityMapView";
import { ChannelHeatmapView } from "twin/channels/ChannelHeatmapView";
import { JourneysView } from "twin/journeys/JourneysView";
import { ScenariosView } from "twin/scenarios/ScenariosView";
import { WarRoomView } from "twin/warroom/WarRoomView";
import { TestRunsView } from "twin/testruns/TestRunsView";
import { FragilityRadarView } from "twin/fragility/FragilityRadarView";
import { TelemetryView } from "twin/telemetry/TelemetryView";

export function App() {
  return (
    <ToastProvider>
      <ToastHost />
      {/* P415: the regime's words reach every translated string from
          here. Inside the query provider because it reads the bank's
          profile; outside the routes because a term is not a property
          of the page you happen to be on. */}
      <RegimeTerms>
        <AppRoutes />
      </RegimeTerms>
    </ToastProvider>
  );
}

/**
 * Which home the viewer lands on (B2).
 *
 * The Resilience Lead landed on the generic home, which named her
 * nowhere and told her nothing about what she was being asked to do.
 * The index route now asks who is looking.
 *
 * Deliberately a switch on ROLE rather than a preference: this is not a
 * dashboard someone assembles, it is the product knowing whose desk it
 * is. Everyone else keeps the home they had.
 */
function IndexHome() {
  const me = useMe();
  // Hold until /me settles, or the wrong home flashes first (P390).
  if (!me.data) return null;
  // Navigation re-cut, option A: every seat's home opens on the five
  // questions, above the cards that home already carried.
  // Round 19 H56 -- a test id for the phone drive to measure home by.
  return (
    <div data-testid="home">
      <FiveQuestions />
      <SeatHome />
    </div>
  );
}

function SeatHome() {
  const me = useMe();
  if (!me.data) return null;
  if (canRunOperations(me.data) && !me.data.is_admin) {
    return <OpsResHome />;
  }
  if (ownsSomething(me.data) && !me.data.is_admin) {
    return <ServiceOwnerHome />;
  }
  return (
    <Home
      bannerSlot={
        <>
          <SinceLastLooked surface="home" />
          <CascadeHomeCard />
        </>
      }
      metricsSlot={<ResilienceTrendCard />}
      mvbSlot={<MvbScorecardCard />}
      queueSlot={<PositionQueueWidget tone="light" />}
      adminSlot={
        <>
          <AccountableExecutiveNote />
          <MyWorkCard />
          <RecommendationsCard />
          <ReadinessCard />
          <AssessmentCyclesCard />
          <BoardSnapshotCard />
          <AssuranceCard />
          <BenchmarkCard />
          <DossierCard />
          <ExecutiveSectionNote />
        </>
      }
    />
  );
}

/**
 * Round 10 A3 -- one line, once, saying where these four cards also
 * live.
 *
 * The Executive section is a real nav item that nothing in the product
 * announces, including the page carrying the same four cards it holds.
 * Shown only to a seat that can actually open something there: a
 * signpost to a room that would refuse the reader is worse than none.
 */
function ExecutiveSectionNote() {
  const { t } = useTranslation();
  const me = useMe();
  if (!canSeeExecutive(me.data)) return null;
  return (
    <p data-testid="home-executive-note" className="text-xs text-slate-500">
      {t("executive.homeNote")}{" "}
      <Link
        to="/executive"
        data-testid="home-executive-link"
        className="text-brand-brassDark underline decoration-dotted underline-offset-2"
      >
        {t("executive.homeNoteLink")}
      </Link>
    </p>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        element={
          <AppShell headerSlot={<PositionQueueWidget />} heroSlot={<HomeHero />} />
        }
      >
        <Route index element={<IndexHome />} />
        <Route path="my-work" element={<MyWorkView />} />
        <Route path="getting-started" element={<GettingStartedView />} />
        {/* Stitching review: the build-out ladder retired to Admin as
            "Set up" (docs/the-story.md, section 1). The old address
            still lands, query and all, so a bookmark or a hand-over
            link keeps working. */}
        <Route path="admin/setup" element={<JourneyView />} />
        <Route path="journey" element={<JourneyRedirect />} />
        <Route path="executive" element={<ExecutiveHome />} />
        <Route path="glossary" element={<GlossaryView />} />
        <Route path="board" element={<BoardSnapshotView />} />
        <Route path="assurance" element={<AssuranceView />} />
        <Route path="recommendations" element={<RecommendationsView />} />
        <Route path="readiness" element={<ReadinessView />} />
        <Route path="readiness/basel" element={<BaselCrosswalkView />} />
        <Route path="readiness/score-methodology" element={<ScoreMethodologyView />} />
        <Route path="benchmark" element={<SectorBenchmarkView />} />
        <Route path="search" element={<SearchView />} />
        <Route path="admin" element={<AdminHome />} />
        <Route path="admin/users" element={<UsersView />} />
        <Route path="admin/audit-events" element={<AuditEventsView />} />
        <Route path="admin/connectors" element={<ConnectorsView />} />
        <Route path="admin/settings" element={<SettingsAdminView />} />
        <Route path="admin/license" element={<LicensePanel />} />
        <Route path="admin/bank-setup" element={<BankSetupView />} />
        <Route path="admin/connections/setup" element={<ConnectionSetupView />} />
        <Route path="admin/obligation-engine" element={<ObligationEngineView />} />
        <Route path="admin/twin-derive" element={<TwinDeriveView />} />
        <Route path="admin/engine-runs" element={<EngineRunsConsole />} />
        <Route path="admin/engine-activity" element={<EngineActivityLedgerView />} />
        <Route path="admin/twin-calibration" element={<TwinCalibrationView />} />
        <Route path="admin/data-dictionary" element={<DataDictionaryView />} />
        <Route path="admin/data-import" element={<DataImportView />} />
        <Route path="admin/data-baseline" element={<DataBaselineView />} />
        <Route path="admin/triggers" element={<TriggersView />} />
        <Route path="admin/edge-delays" element={<EdgeDelaysView />} />
        <Route path="admin/documents" element={<DocumentsView />} />
        <Route path="admin/framework-import" element={<FrameworkImportView />} />
        <Route path="admin/service-accounts" element={<ServiceAccountsView />} />
        <Route path="admin/examiner-grants" element={<ExaminerGrantsView />} />
        <Route path="admin/mock-exam" element={<MockExamView />} />
        <Route path="foundation" element={<FoundationHome />} />
        <Route path="foundation/mvb" element={<MvbDeclarationsRegister />} />
        <Route path="foundation/mvb/:id" element={<MvbDeclarationDetail />} />
        <Route path="foundation/admin/vendors" element={<VendorAdminPage />} />
        <Route path="foundation/admin/ibses" element={<IBSAdminPage />} />
        <Route path="foundation/admin/services" element={<ServiceAdminPage />} />
        <Route path="foundation/admin/capabilities" element={<CapabilityAdminPage />} />
        <Route path="foundation/admin/products" element={<ProductAdminPage />} />
        <Route
          path="foundation/admin/configuration-items"
          element={<ConfigurationItemAdminPage />}
        />
        <Route path="foundation/admin/channels" element={<ChannelAdminPage />} />
        <Route path="foundation/admin/journeys" element={<JourneyAdminPage />} />
        <Route path="foundation/admin/data-assets" element={<DataAssetAdminPage />} />
        <Route path="foundation/admin/ibs-groups" element={<IBSGroupAdminPage />} />
        <Route path="foundation/admin/ea-import" element={<EaImportAdminPage />} />
        <Route path="foundation/admin/floor-list" element={<FloorListAdminPage />} />
        <Route
          path="foundation/admin/funding-resources"
          element={<FundingResourceAdminPage />}
        />
        <Route
          path="foundation/admin/regulatory-commitments"
          element={<RegulatoryCommitmentAdminPage />}
        />
        {/* Round 13 D9 -- P371 modelled both and neither had a screen,
            so a CSV import of either ended at a past participle and the
            capacity bounding the MVB recovery plan could not be read by
            the person accountable for it. */}
        <Route
          path="foundation/admin/recovery-teams"
          element={<RecoveryTeamAdminPage />}
        />
        <Route path="foundation/admin/facilities" element={<FacilityAdminPage />} />
        <Route
          path="foundation/admin/risk-appetite-statements"
          element={<RiskAppetiteAdminPage />}
        />
        <Route
          path="foundation/admin/outsourcing-contracts"
          element={<OutsourcingContractAdminPage />}
        />
        <Route path="twin" element={<TwinHome />} />
        <Route path="twin/cascade" element={<CascadeView />} />
        {/* Round 10 A6 -- one capability surface, one live state, one
            name. The tier grid asked whether a CAPABILITY carries
            findings, and findings land on configuration items and
            services: zero of the estate's open findings have ever been
            on a capability, so that page reported eighteen capabilities
            CLEAN and green during an incident with six IBSes red. It is
            the defect P88 fixed on /twin/capmap, left standing on its
            neighbour, and the two were both called "Capability map" in
            one navigation.

            Redirected rather than removed: the route was linked from
            three surfaces and is in people's bookmarks, and a 404 is a
            worse answer than the right page. */}
        <Route
          path="twin/capability-map"
          element={<Navigate to="/twin/capmap" replace />}
        />
        <Route
          path="twin/landscape/:catalogCode/classic"
          element={<BianLandscapeClassicView />}
        />
        <Route path="twin/landscape/:catalogCode" element={<BianLandscapeView />} />
        <Route path="twin/capmap" element={<KsaCapabilityMapView />} />
        <Route path="twin/constellation" element={<ConstellationView />} />
        <Route path="twin/channels" element={<ChannelHeatmapView />} />
        <Route path="twin/journeys" element={<JourneysView />} />
        <Route path="twin/scenarios" element={<ScenariosView />} />
        <Route path="twin/drills" element={<DrillsView />} />
        <Route path="twin/war-room" element={<WarRoomView />} />
        <Route path="twin/test-runs" element={<TestRunsView />} />
        <Route path="twin/fragility" element={<FragilityRadarView />} />
        <Route path="twin/telemetry" element={<TelemetryView />} />
        <Route path="twin/scenarios/:scenarioId" element={<ScenariosView />} />
        <Route path="twin/ibs-admission" element={<IBSAdmissionView />} />
        <Route path="twin/cifs" element={<CifRegisterView />} />
        <Route path="twin/mvb-candidates" element={<MvbCandidatesView />} />
        <Route path="twin/mvb-recovery" element={<MvbRecoveryView />} />
        <Route path="twin/mvb-target-state" element={<TargetStateView />} />
        <Route path="twin/continuity-posture" element={<ContinuityPostureView />} />
        <Route path="twin/technology-currency" element={<TechnologyCurrencyView />} />
        <Route
          path="foundation/admin/technology-lifecycle"
          element={<TechnologyLifecycleAdminPage />}
        />
        <Route path="twin/service-disposition" element={<ServiceDispositionView />} />
        <Route
          path="twin/architecture-standards"
          element={<ArchitectureStandardsView />}
        />
        <Route path="twin/continuity-plans" element={<ContinuityPlansView />} />
        <Route path="twin/changes" element={<ChangeRegisterView />} />
        <Route path="twin/tolerance-metrics" element={<ToleranceMetricsView />} />
        <Route path="twin/mvb-drill" element={<MvbDrillView />} />
        <Route path="twin/field-exam" element={<FieldExamView />} />
        <Route path="twin/ibs-portfolio" element={<IBSPortfolioView />} />
        <Route path="twin/ibs-groups" element={<IBSGroupsView />} />
        <Route path="twin/incident-reports" element={<IncidentReportRegisterView />} />
        <Route path="twin/ibs/:id" element={<IBSDetail />} />
        <Route path="twin/service/:id" element={<ServiceDetail />} />
        <Route path="twin/capability/:id" element={<CapabilityDetail />} />
        <Route path="twin/vendor/:id" element={<VendorDetail />} />
        <Route path="twin/ci/:id" element={<ConfigurationItemDetail />} />
        <Route path="twin/findings" element={<OpenFindingsView />} />
        <Route path="people" element={<PeopleView />} />
        <Route path="twin/findings/new" element={<RaiseFindingView />} />
        <Route path="twin/findings/:findingId" element={<FindingDetail />} />
        <Route path="twin/evidence/:artefactId" element={<EvidenceArtefactDetail />} />
        <Route path="framework" element={<FrameworkHome />} />
        <Route path="framework/letters" element={<LettersRegisterView />} />
        <Route path="framework/letters/new" element={<LetterIntakeView />} />
        <Route path="framework/letters/:letterId" element={<LetterDetailView />} />
        <Route path="framework/queue/:position" element={<QueueView />} />
        <Route path="framework/caps/:capId" element={<CAPDetailView />} />
        <Route path="framework/attestations" element={<AttestationsView />} />
        <Route path="framework/regulatory-change" element={<RegulatoryChangeView />} />
        <Route path="framework/document-gap" element={<DocumentGapView />} />
        <Route path="framework/outbound-feed" element={<OutboundFeedView />} />
        <Route path="framework/assessments" element={<FrameworkAssessmentsView />} />
        <Route
          path="framework/assessments/:frameworkCode"
          element={<FrameworkAssessmentsView />}
        />
        <Route
          path="framework/assessments/:frameworkCode/cycles/:cycleId"
          element={<CycleAssessmentGridView />}
        />
        <Route
          path="framework/assessments/:frameworkCode/cycles/:cycleId/analyser"
          element={<AnalyserPanel />}
        />
        {/* E2 -- a mistyped or stale URL used to render a
            completely blank page: no chrome, no nav, no way back.
            Inside the shell, so the navigation survives. */}
        <Route path="*" element={<NotFoundView />} />
      </Route>
    </Routes>
  );
}
